import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const IndexingPage = () => (
  <StaticPageLayout
    overline="About"
    title="Indexing & Metrics"
    description="MAH Quantum Research Institute is designed to meet global indexing standards and ensure broad research discoverability. Publications are assigned DOIs and archived via Zenodo."
    breadcrumbs={[{ label: 'Indexing & Metrics', path: '/indexing' }]}
  >
    <div className="space-y-10">

      <div className="bg-blue-50 border border-blue-200 px-5 py-4">
        <p className="text-sm text-[#174EA6] leading-relaxed">
          <strong>Important Notice:</strong> The platform is currently in its demonstration phase. Formal indexing registrations with databases such as Scopus, Web of Science, or similar services have not yet been completed. The statements below describe the platform's discoverability approach and future indexing goals.
        </p>
      </div>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Discoverability Approach</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Designed to meet global indexing standards and ensure broad research discoverability, the platform's publication infrastructure is structured to support inclusion in major academic databases upon formal launch.
        </p>
        <p className="text-[#4B5563] leading-relaxed">
          Publications are assigned DOIs and archived via Zenodo, enabling discoverability through platforms like Google Scholar and other academic search engines that index open repositories.
        </p>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-5 pb-3 border-b border-[#E5E7EB]">DOI Assignment</h2>
        <div className="border border-[#E5E7EB] bg-white p-6">
          <p className="text-[#4B5563] leading-relaxed mb-3">
            Upon acceptance, each published article will be assigned a <strong>Digital Object Identifier (DOI)</strong> — a persistent, globally unique identifier that enables permanent linking and citation of the published work.
          </p>
          <p className="text-[#4B5563] leading-relaxed">
            DOIs ensure that published research remains permanently citable and accessible, even if URLs change over time. This is a foundational requirement for academic citation standards.
          </p>
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-5 pb-3 border-b border-[#E5E7EB]">Zenodo Archiving</h2>
        <div className="border border-[#E5E7EB] bg-white p-6">
          <p className="text-[#4B5563] leading-relaxed mb-3">
            Accepted publications are assigned DOIs and archived via Zenodo to ensure open access and long-term availability. Zenodo is an open-access repository operated by CERN, widely recognised by the global research community as a trusted long-term archive.
          </p>
          <p className="text-[#4B5563] leading-relaxed">
            Zenodo-archived publications are indexed by Google Scholar and other scholarly search systems that support OAI-PMH protocols, ensuring the research remains discoverable by the global academic community.
          </p>
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">ISSN Registration</h2>
        <p className="text-[#4B5563] leading-relaxed">
          Our journals will be officially registered with ISSN upon formal release. ISSN (International Standard Serial Number) registration provides each journal with a unique identifier recognised internationally. Registration is being prepared as part of the platform's formal launch process.
        </p>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-4 pb-3 border-b border-[#E5E7EB]">Future Indexing Goals</h2>
        <p className="text-[#4B5563] leading-relaxed mb-4">
          Upon formal launch and accumulation of a sufficient publication record, the institute intends to apply for inclusion in major academic indexing services. Eligibility criteria for these services include demonstrated peer review standards, publication consistency, and editorial quality — all of which are being structured into the platform from inception.
        </p>
        <div className="bg-[#F5F7FA] border border-[#E5E7EB] px-5 py-4">
          <p className="text-sm text-[#6B7280] italic">
            Note: Indexing in databases such as Scopus, Web of Science, or PubMed requires formal application and review by those organisations. The platform does not currently claim indexing in these services.
          </p>
        </div>
      </section>
    </div>
  </StaticPageLayout>
);

export default IndexingPage;
